import { useLoader } from '@react-three/fiber';
import { useSyncExternalStore } from 'react';
import { ImageLoader, Loader, Texture } from 'three';
import { captureException } from 'utils/captureException';

interface DeviceImageLoader extends Loader {
  // oxlint-disable-next-line @typescript-eslint/no-misused-new
  new (): DeviceImageLoader;

  /** White space around image */
  gutter?: number;
  load: (src: string) => void;
  loadAsync: (src: string) => Promise<Texture>;
}

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;
const MAX_DELAYED_RETRIES = 3;
const DELAYED_RETRY_MS = 2_000;
const reportedSources = new Set<string>();
const failedSources = new Map<
  string,
  { texture?: Texture; retryCount: number; timer?: ReturnType<typeof setTimeout> }
>();
const retryListeners = new Set<() => void>();
let retryVersion = 0;

const subscribeToRetries = (listener: () => void) => {
  retryListeners.add(listener);
  return () => {
    retryListeners.delete(listener);
  };
};

const getRetryVersion = () => retryVersion;

const retrySources = (sources: Iterable<string>) => {
  for (const src of sources) {
    const retry = failedSources.get(src);
    if (!retry?.texture) continue;
    clearTimeout(retry.timer);
    retry.timer = undefined;
    useLoader.clear(DeviceImageLoader, src);
    retry.texture.dispose();
    retry.texture = undefined;
  }
  retryVersion++;
  retryListeners.forEach((listener) => listener());
};

const retryFailedSources = () => retrySources(failedSources.keys());

const resetRetries = (src: string) => {
  const retry = failedSources.get(src);
  clearTimeout(retry?.timer);
  retry?.texture?.dispose();
  failedSources.delete(src);
};

// Clear failed preload results too, so mounted hooks and future consumers load the source again.
const scheduleRetry = (src: string, texture: Texture) => {
  const retry = failedSources.get(src) ?? { retryCount: 0 };
  retry.texture = texture;
  failedSources.set(src, retry);
  // An offline browser gets the retry when connectivity returns. The listener also covers exhausted delayed retries.
  window.addEventListener('online', retryFailedSources, { once: true });

  // A failure while online can be temporary, for example a CDN or DNS outage. Retry after 2, 4, and 6 seconds.
  if (!navigator.onLine || retry.retryCount >= MAX_DELAYED_RETRIES || retry.timer !== undefined) return;

  retry.timer = setTimeout(
    () => {
      retry.timer = undefined;
      if (!navigator.onLine) return;
      retry.retryCount++;
      retrySources([src]);
    },
    DELAYED_RETRY_MS * (retry.retryCount + 1)
  );
};

// Cache storage is optional; failures must not prevent an image from loading.
const tryCache = async <T>(operation: () => Promise<T>) => {
  try {
    return await operation();
  } catch (error) {
    console.warn(error);
  }
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const loadImage = async (imageLoader: ImageLoader, src: string) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await imageLoader.loadAsync(src);
    } catch (e) {
      // Retries cannot help while the browser is offline.
      if (!navigator.onLine || attempt >= MAX_RETRIES) throw e;
      await wait(RETRY_DELAY_MS * (attempt + 1));
    }
  }
};

// Report each failed source once for each session. Offline users cause expected failures.
const reportLoadError = (src: string, cause: unknown) => {
  if (!navigator.onLine || reportedSources.has(src)) return;

  reportedSources.add(src);
  captureException(new Error('Device image failed to load', { cause }), {
    captureContext: { extra: { src: src.slice(0, 200) } },
  });
};

const DeviceImageLoader = function () {
  const loader = new Loader() as DeviceImageLoader;

  loader.load = function load(
    src: string,
    onLoad?: (texture: Texture) => void,
    _onProgress?: (event: ProgressEvent) => void,
    onError?: (event: ErrorEvent) => void
  ) {
    void this.loadAsync(src).then(onLoad).catch(onError);
  };

  loader.loadAsync = async function loadAsync(src: string) {
    this.manager.itemStart(src);

    try {
      const texture = new Texture();
      const imageLoader = new ImageLoader(this.manager);
      imageLoader.setCrossOrigin(this.crossOrigin);
      imageLoader.setPath(this.path);

      const cache = 'caches' in window ? await tryCache(() => window.caches.open('device-image')) : undefined;
      const url = src.replace(/^data:image\/[^;]+;base64,/, 'https://');

      const item = cache && (await tryCache(() => cache.match(url)));

      if (item) {
        try {
          // Retrying the same corrupt data cannot repair it; recover from the source immediately.
          texture.image = await imageLoader.loadAsync(await item.text());
          texture.needsUpdate = true;
          resetRetries(src);
          return texture;
        } catch {
          // The cached entry is not valid. Delete it and load from the network.
          await tryCache(() => cache.delete(new Request(url)));
        }
      }

      let image;

      try {
        image = await loadImage(imageLoader, src);
        resetRetries(src);
      } catch (e) {
        reportLoadError(src, e);
        scheduleRetry(src, texture);
        return texture;
      }

      const imageWidth = image.width;
      const imageHeight = image.height;
      const size = Math.max(imageWidth, imageHeight);
      const gutter = this.gutter ?? size / 5;
      const fullSize = size + gutter;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;

      const scale = Math.max(1, window.devicePixelRatio);
      const canvasSize = fullSize * scale;
      canvas.width = canvasSize;
      canvas.height = canvasSize;
      ctx.scale(scale, scale);

      // Fill canvas with background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvasSize, canvasSize);

      // Resize and draw image in the center
      ctx.drawImage(image, (fullSize - imageWidth) / 2, (fullSize - imageHeight) / 2, imageWidth, imageHeight);

      texture.image = await imageLoader.loadAsync(canvas.toDataURL());

      canvas.remove();

      if (cache) {
        const textureImage = texture.image as HTMLImageElement;
        await tryCache(() => cache.put(new Request(url), new Response(textureImage.src)));
      }

      texture.needsUpdate = true;
      return texture;
    } finally {
      this.manager.itemEnd(src);
    }
  };

  return loader;
} as unknown as DeviceImageLoader;

export const useDeviceImage = (src: string, gutter?: number) => {
  useSyncExternalStore(subscribeToRetries, getRetryVersion, getRetryVersion);
  return useLoader(DeviceImageLoader, src, (loader) => {
    (loader as DeviceImageLoader).gutter = gutter;
  });
};

useDeviceImage.preload = (src: string) => useLoader.preload(DeviceImageLoader, src);
